import React, { forwardRef, useMemo } from 'react';
import { animationTransition } from '@shared/utils/animationTransition';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { Clock, Diamond } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { BOX_SHADOW_8_14_26 } from '@apps/card/constants/boxShadow';
import { Skeleton } from '@shared/components/Skeleton';
import { AspectRatioImage } from '../AspectRatioImage';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import useFavoriting from '../DesignsGallery/useFavoriting';
import { FavoriteButton } from '@apps/card/components/FavoriteButton/FavoriteButton';
import { FoilBadge } from '../FoilBadge';
import type { StationeryTemplate } from '../DesignsGallery/DesignsGallery.types';
import type { As } from '@shared/utils/forwardRef';

const AssetPreviewFrame: React.FC = ({ children }) => {
  const isMobile = useIsMobileScreen();
  return (
    <Flex
      aspectRatio={isMobile ? '8 / 10' : '9 / 10'}
      overflow="hidden" // Needed for Safari
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      position="relative" // For badges
      paddingX={isMobile ? 4 : 8}
      paddingY={8}
      background="hsla(45, 12%, 93%, 1)"
      transition={animationTransition('background')}
      _groupHover={{
        background: 'hsla(45, 12%, 73%, 0.4)',
      }}
    >
      {children}
    </Flex>
  );
};

type AssetPreviewProps = Readonly<{
  assetUrl: string;
}>;

export const AssetPreview: React.FC<AssetPreviewProps> & { Skeleton: React.FC } = props => {
  const { assetUrl, children } = props;
  return (
    <AssetPreviewFrame>
      <AspectRatioImage
        src={assetUrl}
        borderRadius={1}
        boxShadow={BOX_SHADOW_8_14_26}
        transition={animationTransition('box-shadow')}
        _groupHover={{
          boxShadow: BOX_SHADOW_8_14_26,
        }}
      />
      {children}
    </AssetPreviewFrame>
  );
};

const AssetPreviewSkeleton: React.FC = () => {
  // Matches AssetPreviewFrame dimensions.
  const isMobile = useIsMobileScreen();
  const aspectRatio = isMobile ? '8 / 10' : '9 / 10';

  return <Skeleton aspectRatio={aspectRatio} />;
};

AssetPreview.Skeleton = AssetPreviewSkeleton;

export type TagVariant = 'comingSoon' | 'printAvailable' | 'disabled' | 'printAndDigitalAvailable' | 'digitalOnly' | 'printAndPremiumDigitalAvailable';

type TagProps = Readonly<{
  variant: TagVariant;
}>;

const Tag = (props: TagProps) => {
  const { tq } = useTranslation('stationery');

  const TAG_VARIANT_MAP: Record<TagProps['variant'], { Icon: () => JSX.Element | null; label: string }> = useMemo(
    () => ({
      comingSoon: {
        Icon: () => (
          <Clock
            size="md"
            color="positive7"
          />
        ),
        label: tq`Coming Soon`,
      },
      printAvailable: {
        Icon: () => null,
        label: tq`Paper Only`,
      },
      printAndPremiumDigitalAvailable: {
        Icon: () => null,
        label: tq`Paper & Digital`,
      },
      disabled: {
        Icon: () => (
          <Clock
            size="md"
            color="positive7"
          />
        ),
        label: tq`Disabled (Admin View Only)`,
      },
      printAndDigitalAvailable: {
        Icon: () => null,
        label: tq`Paper & Digital`,
      },
      digitalOnly: {
        Icon: () => null,
        label: tq`Digital Only`,
      },
    }),
    [tq],
  );

  const { Icon, label } = TAG_VARIANT_MAP[props.variant];
  return (
    <Flex
      alignItems="center"
      columnGap={1}
      paddingX={3}
      paddingY={1}
      borderRadius={40}
      backgroundColor="mono2"
    >
      <Icon />
      <TextV2
        as="span"
        typographyVariant="label2"
      >
        {label}
      </TextV2>
    </Flex>
  );
};

type ContentContainerProps = Readonly<{
  alignment: 'left' | 'center';
  header: React.ReactNode;
  height?: BoxProps['height'];
}>;

const ContentContainer: React.FC<ContentContainerProps> = ({ alignment, header, height, children }) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={{ _: 1, md: 3 }}
      alignItems={alignment === 'left' ? 'flex-start' : 'center'}
      height={height}
    >
      <Flex
        flexDirection="column"
        rowGap={2}
        alignItems={alignment === 'left' ? 'flex-start' : 'center'}
        textAlign={alignment}
      >
        {header}
      </Flex>
      {children}
    </Flex>
  );
};

type ContentProps = Readonly<{
  brow?: string;
  title?: string;
  label?: string | JSX.Element;
  alignment?: 'left' | 'center';
  /** Slot rendered directly under the title. */
  belowTitle?: JSX.Element;
}>;

type ContentSkeletonProps = Readonly<{
  alignment?: 'left' | 'center';
}>;

const Content: React.FC<ContentProps> & { Skeleton: React.FC<ContentSkeletonProps> } = ({ children, brow, label, title, belowTitle, alignment = 'center' }) => {
  return (
    <ContentContainer
      alignment={alignment}
      header={
        <>
          {!!brow && (
            <TextV2
              typographyVariant="label2"
              color="mono12"
            >
              {brow}
            </TextV2>
          )}
          {!!title && <TextV2 typographyVariant={{ _: 'hed1', md: 'hed2' }}>{title}</TextV2>}
          {belowTitle}
          {label !== undefined && (typeof label === 'string' ? <TextV2 typographyVariant="label2">{label}</TextV2> : <div>{label}</div>)}
        </>
      }
    >
      {children}
    </ContentContainer>
  );
};

const ContentSkeleton: React.FC<ContentSkeletonProps> = ({ children, alignment = 'center' }) => {
  return (
    <ContentContainer
      height={80}
      alignment={alignment}
      header={
        <>
          <Skeleton
            width={120}
            height={18}
          />
          <Skeleton
            width={50}
            height={16}
          />
        </>
      }
    >
      {children}
    </ContentContainer>
  );
};

Content.Skeleton = ContentSkeleton;

export const GalleryTile = <T extends As = 'div'>(props: BoxProps<T>) => {
  return (
    <Box
      display="grid"
      alignItems="start"
      role="group"
      position="relative"
      // A grid item defaults to `min-width/height: auto`, i.e. it refuses to shrink below its
      // min-content. Safari (WebKit) inflates that automatic minimum for an `aspect-ratio` item
      // with fixed-size content (the 5x7 card canvas), so the tile can overflow its (fixed
      // `calc()`) track. Pinning both minimums to 0 lets the item honor the equal track width
      // instead of spilling — defense-in-depth alongside the fixed tracks in Gallery.tsx.
      minWidth={0}
      minHeight={0}
      cursor={props.onClick ? 'pointer' : undefined}
      {...props}
    />
  );
};

// Compound components + forwardRef + Typescript = a lot of extra typing, so working around it.
export const GalleryTileWithRef = forwardRef(<T extends As = 'div'>(props: BoxProps<T>, ref: React.Ref<HTMLDivElement>) => {
  return (
    <Box
      display="grid"
      alignItems="start"
      role="group"
      position="relative"
      // See GalleryTile above: min-width/height 0 keeps the aspect-ratio tile from overflowing
      // its fixed track in Safari.
      minWidth={0}
      minHeight={0}
      cursor={props.onClick ? 'pointer' : undefined}
      {...props}
      ref={ref}
    />
  );
});

const PremiumBadge = () => {
  const { tq } = useTranslation('stationery');
  return (
    <Box
      typographyVariant="label1"
      fontWeight={600}
      position="absolute"
      display={'flex'}
      alignItems="center"
      right={4}
      bottom={4}
      color="white"
      userSelect="none"
      zIndex={1}
      title={tq`Premium Digital Available`}
    >
      <Diamond
        color="hsl(40, 60%, 60%)"
        size={20}
        transform="translateY(1px)"
      />
    </Box>
  );
};

type FavoriteBadgeProps = Readonly<{
  eventId?: string;
  template: StationeryTemplate;
  onToggle?: (isFavorite: boolean) => void;
}>;

const FavoriteBadge: React.FC<FavoriteBadgeProps> = ({ eventId, onToggle, template }) => {
  const { isFavorite, toggleFavorite, isLoading } = useFavoriting({
    isFavorite: template.isFavorite,
    templateId: template.id,
    eventId: eventId || '',
  });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    onToggle?.(!isFavorite);

    toggleFavorite();
  };

  if (!eventId) {
    return null;
  }

  return (
    <FavoriteButton
      position="absolute"
      top={3}
      right={3}
      isFavorite={isFavorite}
      isLoading={isLoading}
      onClick={handleClick}
    />
  );
};

const TileFoilBadge = () => {
  return (
    <Box
      position="absolute"
      top={4}
      left={3}
    >
      <FoilBadge />
    </Box>
  );
};

GalleryTile.Skeleton = () => {
  return (
    <GalleryTile rowGap={6}>
      <AssetPreview.Skeleton />
      <Content.Skeleton>
        <Skeleton
          width={70}
          height={18}
        />
      </Content.Skeleton>
    </GalleryTile>
  );
};

GalleryTile.AssetPreview = AssetPreview;
GalleryTile.Content = Content;
GalleryTile.Tag = Tag;
GalleryTile.PremiumBadge = PremiumBadge;
GalleryTile.FavoriteBadge = FavoriteBadge;
GalleryTile.FoilBadge = TileFoilBadge;
